import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';

import { useUtmParams } from '@/hooks/useUtmParams';
import { parseSearchParams } from '@/utils/stringUtils';
import { UtmParams } from '@/store/slices/utm';

/**
 * Component to capture UTM parameters from URL on app initialization
 * Should be placed high in the component tree to capture UTM params early
 */
const UtmCapture: React.FC = () => {
  const location = useLocation();
  const { setUtmParams, utmParams } = useUtmParams();

  useEffect(() => {
    // Only capture UTM params if we don't already have them stored
    // This prevents overwriting existing UTM params when navigating within the app
    const hasExistingUtmParams = Object.values(utmParams).some(Boolean);

    if (!hasExistingUtmParams) {
      const searchParams = parseSearchParams(location.search);
      const extractedUtmParams: UtmParams = {
        utmSource: searchParams.utm_source || undefined,
        utmMedium: searchParams.utm_medium || undefined,
        utmCampaign: searchParams.utm_campaign || undefined,
        utmTerm: searchParams.utm_term || undefined,
        utmContent: searchParams.utm_content || undefined,
        utmId: searchParams.utm_id || undefined,
      };

      // Only set if we found any UTM params
      const hasNewUtmParams = Object.values(extractedUtmParams).some(Boolean);
      if (hasNewUtmParams) {
        extractedUtmParams.utmCampaignDate = new Date().toISOString();
        setUtmParams(extractedUtmParams);
      }
    }
  }, [location.search, setUtmParams, utmParams]);

  return null;
};

export default UtmCapture;
